import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Search, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.users || d?.results || []);
const roleColor = { admin: "bg-destructive/10 text-destructive", fisioterapista: "bg-primary/10 text-primary", paziente: "bg-accent/20 text-accent-foreground" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", access_code: "", full_name: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setUsers(asList(await api.adminUsers())); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createPhysio = async () => {
    if (!form.username || !form.access_code) { toast.error("Username e codice obbligatori"); return; }
    setSaving(true);
    try {
      await api.createPhysio({ username: form.username, access_code: form.access_code, full_name: form.full_name || undefined });
      toast.success("Fisioterapista creato");
      setForm({ username: "", access_code: "", full_name: "" }); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const reset = async (id) => {
    try {
      const res = await api.resetPassword(id);
      toast.success(res?.access_code ? `Nuovo codice: ${res.access_code}` : "Codice reimpostato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removePhysio = async (id, name) => {
    try {
      await api.deletePhysio(id);
      toast.success(`Fisioterapista ${name} eliminato`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = users.filter((u) => (u.full_name || u.username || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader
        title="Gestione utenti"
        subtitle="Amministra utenti, fisioterapisti e credenziali."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="create-physio-button"><Plus className="mr-2 h-4 w-4" /> Nuovo fisioterapista</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Crea fisioterapista</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Nome completo</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5" data-testid="physio-name-input" /></div>
                <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1.5" data-testid="physio-username-input" /></div>
                <div><Label>Codice di accesso</Label><Input value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value })} className="mt-1.5" data-testid="physio-code-input" /></div>
              </div>
              <DialogFooter><Button onClick={createPhysio} disabled={saving} data-testid="save-physio-button">{saving ? "Creazione..." : "Crea"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca utente..." className="pl-9" data-testid="user-search" />
      </div>

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> :
        filtered.length === 0 ? <EmptyState title="Nessun utente" /> : (
          <div className="grid gap-3" data-testid="users-list">
            {filtered.map((u, i) => {
              const id = u.id || u._id;
              const name = u.full_name || u.username;
              const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
              return (
                <Card key={id || i} className="border-border/70">
                  <CardContent className="flex flex-wrap items-center gap-4 p-4">
                    <Avatar className="h-11 w-11 bg-muted"><AvatarFallback className="bg-transparent font-semibold">{initials}</AvatarFallback></Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{name}</p>
                      <p className="text-xs text-muted-foreground">@{u.username}</p>
                    </div>
                    {u.role && <span className={`rounded-full px-3 py-1 text-xs font-medium ${roleColor[u.role] || "bg-muted"}`}>{u.role}</span>}
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="outline" size="sm" data-testid={`reset-password-${i}`}><KeyRound className="mr-1 h-3.5 w-3.5" /> Reset codice</Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Reimpostare il codice?</AlertDialogTitle><AlertDialogDescription>Verrà generato un nuovo codice di accesso per {name}.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => reset(id)}>Reimposta</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    {u.role === "fisioterapista" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-physio-${i}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Elimina</Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Eliminare il fisioterapista?</AlertDialogTitle><AlertDialogDescription>Il profilo di {name} verrà eliminato definitivamente. Questa azione non può essere annullata.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => removePhysio(id, name)}>Elimina</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}
